import { useEffect, useState, useCallback } from 'react';
import { usePlugins, type Setting } from '@morph-mapper/plugins';
import { useSetupStore } from '../../store';
import {
  setAllPluginsEnabled,
  togglePluginEnabled,
  withPluginSetting,
} from '../../../../utils/plugin-config';

interface SelectedPlugin {
  key: string;
  name: string;
  version: string;
  settings: Setting[];
  enabled: boolean;
}

export const usePluginSettings = () => {
  const [pluginsState, setPluginsState] = useState<SelectedPlugin[]>([]);

  const [step, setCurrentStep, templateId] = useSetupStore((s) => [
    s.currentStep,
    s.setCurrentStep,
    s.getSelectedTemplateId(),
  ]);

  const { preConfig: rawPreConfig, hydrate } = usePlugins();

  const setPluginSetting = (
    pluginKey: string,
    settingKey: string,
    value: any,
  ) => {
    setPluginsState((prev) =>
      withPluginSetting(prev, pluginKey, settingKey, value),
    );
  };

  const togglePlugin = (pluginKey: string) => {
    setPluginsState((prev) => togglePluginEnabled(prev, pluginKey));
  };

  const setAllPlugins = (enabled: boolean) => {
    setPluginsState((prev) => setAllPluginsEnabled(prev, enabled));
  };

  const progressSetup = useCallback(() => {
    // Apply changes in the pluginsettings to framework
    hydrate(pluginsState);
    setCurrentStep(step + 1);
  }, [step, setCurrentStep, pluginsState, hydrate]);

  useEffect(() => {
    if (!templateId) return;

    let cancelled = false;

    const fetchPlugins = async () => {
      const result = await rawPreConfig(templateId);
      if (!cancelled) {
        setPluginsState(result.plugins);
      }
    };
    fetchPlugins();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  return {
    pluginsState,
    setPluginsState,
    togglePlugin,
    setAllPlugins,
    setPluginSetting,
    progressSetup,
  };
};
